<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { FieldLabel } from '@/shared/ui';

const props = defineProps<{
    modelValue: string | undefined;
    label: string;
    ariaLabel?: string;
    errorMessages?: string | string[];
    min?: Date;
    max?: Date;
    /** 選べる日（`YYYY-MM-DD` を受け取り true なら選べる）。未指定なら min〜max のすべて */
    allowedDates?: (date: string) => boolean;
}>();

const emit = defineEmits<{
    'update:modelValue': [value: string | undefined];
}>();

const menu = ref(false);

// v-date-pickerはDate型を扱うため、string <-> Date変換が必要
const dateValue = computed({
    get: () => {
        if (!props.modelValue) return undefined;
        // YYYY-MM-DD形式からDateオブジェクトに変換
        const parts = props.modelValue.split('-').map(Number);
        const year = parts[0] ?? 0;
        const month = parts[1] ?? 1;
        const day = parts[2] ?? 1;
        return new Date(year, month - 1, day);
    },
    set: (value: Date | undefined) => {
        if (!value) {
            emit('update:modelValue', '');
            return;
        }
        // DateオブジェクトからYYYY-MM-DD形式に変換
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, '0');
        const day = String(value.getDate()).padStart(2, '0');
        emit('update:modelValue', `${year}-${month}-${day}`);
    },
});

// v-date-picker の allowed-dates は Date（または文字列）を渡すので、YYYY-MM-DD にそろえて判定する
const isAllowedDate = (value: unknown): boolean => {
    if (!props.allowedDates) return true;
    if (!(value instanceof Date)) return props.allowedDates(String(value).slice(0, 10));
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return props.allowedDates(`${year}-${month}-${day}`);
};

// 日付選択時にメニューを閉じる
watch(dateValue, () => {
    menu.value = false;
});
</script>

<template>
    <div>
        <FieldLabel v-if="label" :label="label" html />
        <v-menu v-model="menu" :close-on-content-click="false" transition="scale-transition">
            <template #activator="{ props: menuProps }">
                <v-text-field
                    v-bind="menuProps"
                    :model-value="modelValue"
                    :aria-label="ariaLabel || label"
                    :error-messages="errorMessages"
                    :hide-details="!errorMessages"
                    variant="outlined"
                    density="compact"
                    readonly
                    append-inner-icon="mdi-calendar"
                />
            </template>
            <v-date-picker v-model="dateValue" hide-header :min="min" :max="max" :allowed-dates="isAllowedDate" />
        </v-menu>
    </div>
</template>
